// The approver-side rules, pure. Hide-only, never an allow-list: the backend enforces the route
// role, the policy's `required_role` and maker-checker independently and answers 403 — these only
// keep the UI from offering a control that is guaranteed to fail. If this says yes and the server
// says no, the server wins and its sentence is what the user reads.
import type { ApprovalInboxRow } from '../models/platformAction.types'

/**
 * The workspace role ladder, lowest first. Unknown roles rank 0 so they never clear a threshold.
 * `insight-actions` gates on the same ladder and reads it from here — a second copy would drift
 * the first time a rung is added.
 */
export const WORKSPACE_RANK: Readonly<Record<string, number>> = {
  viewer: 1,
  viewer_plus: 2,
  editor: 3,
  admin: 4,
  owner: 5,
}

export type WorkspaceRank = keyof typeof WORKSPACE_RANK

/** Who is looking: the ladder rank already resolved (owner-aware) plus the signed-in user's id. */
export interface ApprovalViewer {
  rank: number
  userId: number | null
}

export function rankOf(role: string): number {
  return WORKSPACE_RANK[role] ?? 0
}

/**
 * The account's own owner carries no shared-access level at all — no row names them — yet they
 * are not below the account's admins. Without this an owner reads as rank 0 and is shown neither
 * a decision nor edit access on their own account.
 */
export function resolveRank(level: unknown, isAccountOwner: boolean): number {
  if (isAccountOwner) return rankOf('owner')
  return rankOf(String(level ?? ''))
}

/** Reads every change, decides none. An unknown role (rank 0) is not called a viewer. */
export function isViewerRank(rank: number): boolean {
  return rank > 0 && rank < rankOf('editor')
}

/** Whether to OFFER Approve / Reject on a row. */
export function canDecide(row: ApprovalInboxRow, viewer: ApprovalViewer): boolean {
  if (row.approvalState !== 'awaiting_decision') return false
  if (viewer.rank < rankOf(row.requiredApproval.requiredRole)) return false
  // Maker-checker: the proposer is never an eligible decider.
  if (
    row.requiredApproval.requiresSecondPerson &&
    row.proposer !== null &&
    row.proposer.id === viewer.userId
  ) {
    return false
  }
  return true
}

/**
 * Whether to OFFER Cancel. `cancellable` is the server's reading of the request's state; while a
 * decision is still open, Reject is the verb that closes the request, so Cancel is not offered
 * beside it.
 */
export function offersCancel(row: ApprovalInboxRow): boolean {
  if (row.approvalState === 'awaiting_confirm') return true
  return row.request.cancellable && row.approvalState !== 'awaiting_decision'
}

/** A viewer decides nothing — cancel included, which is a decision the same way reject is. */
export function showsControls(row: ApprovalInboxRow, viewer: ApprovalViewer): boolean {
  return canDecide(row, viewer) || (!isViewerRank(viewer.rank) && offersCancel(row))
}
